/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useInvoice } from "../lib/react-query/useInvoice";
import useCartItemStatus from "./useCartItemStatus";
import { useProcessedCartEntities } from "./useProcessedCartEntities";
import { INVOICE_STATUS } from "../constants/constants";

export const useCartItemStatusPage = ({ invoiceId, initialStatus = null }) => {
  const {
    data: invoice,
    isLoading: isInvoiceFirstLoad,
    isFetching: isFetchingInvoice,
    isFetchedAfterMount: isInvoiceFetchedAfterMount,
    isError: isInvoiceError,
    error: invoiceError,
  } = useInvoice(
    invoiceId,
    { include: "lines.promotion_instance.print_link" },
    {
      enabled: Boolean(invoiceId),
      refetchInterval: (data) => {
        if (!data?.state?.data) return false;
        const invoiceStatus = data.state.data.status;
        return invoiceStatus === INVOICE_STATUS.PENDING ? 3000 : false;
      },
    }
  );

  // A cached invoice can predate the payment that led here - wait for the mount refetch, not the polls.
  const isLoadingInvoice = isInvoiceFirstLoad || (isFetchingInvoice && !isInvoiceFetchedAfterMount);

  const cartEntities = useProcessedCartEntities(invoice);

  const statusData = useCartItemStatus({
    invoiceData: invoice,
    initialStatus,
  });

  const { isLoading, isError, error, canRender } = useMemo(() => {
    if (initialStatus) {
      return {
        isLoading: false,
        isError: isInvoiceError,
        error: invoiceError,
        canRender: !isInvoiceError,
      };
    }

    const loading = isLoadingInvoice || !statusData.status;

    return {
      isLoading: loading,
      isError: isInvoiceError,
      error: invoiceError,
      canRender: !loading && !isInvoiceError,
    };
  }, [isLoadingInvoice, isInvoiceError, invoiceError, initialStatus, statusData.status]);

  if (!canRender) {
    return {
      isLoading,
      isError,
      error,
      invoice: null,
      cartEntities: {
        items: [],
        currency: null,
      },
      orderStatus: null,
      isOrderConfirmed: false,
      isOrderPending: false,
      isOrderCancelled: false,
      isOrderDelay: false,
      shouldShowOrderDetails: false,
    };
  }

  return {
    isLoading: false,
    isError: false,
    error: null,
    invoice,
    cartEntities,
    orderStatus: statusData.status,
    isOrderConfirmed: statusData.isConfirmed,
    isOrderPending: statusData.isPending,
    isOrderCancelled: statusData.isCancelled,
    isOrderDelay: statusData.isDelay,
    shouldShowOrderDetails: statusData.shouldShowOrderDetails,
  };
};